import { getCountriesList, getStatesList } from "../countryStateData.js"
import { ContactService } from "../services/contactService.js"
import { stripCommasFromName } from "./nameCommaValidation.js"

/**
 * Maps API `ship_to` + contact-like fields into shipping section state.
 * Province/state uses 2-letter codes (`state_code`) via `normalizeProvinceOrState`
 * (same helpers as `Customers.jsx`).
 * @param {object|null|undefined} shipTo
 * @param {{ firstName?: string, lastName?: string, companyName?: string, phone?: string, email?: string }} contact
 */
export async function shippingFieldsFromShipTo(shipTo, contact) {
    const c = contact ?? {}
    const base = {
        firstName: stripCommasFromName(c.firstName ?? ""),
        lastName: stripCommasFromName(c.lastName ?? ""),
        companyName: c.companyName ?? "",
        phone: c.phone ?? "",
        email: c.email ?? "",
    }
    const pick = (raw, fallback) => {
        if (raw == null) return fallback
        const s = String(raw)
        return s.trim() === "" ? fallback : s
    }
    if (!shipTo || typeof shipTo !== "object") {
        return {
            ...base,
            country: "",
            province: "",
            city: "",
            streetAddress: "",
            postalCode: "",
        }
    }

    const countries = getCountriesList()
    const countryName =
        ContactService.normalizeCountry(String(shipTo.country ?? "").trim(), countries) || ""

    let states = []
    if (countryName === "Canada" || countryName === "United States") {
        const co = countries.find((x) => x.name === countryName)
        if (co) {
            states = getStatesList(co.id)
        }
    }

    const rawState = shipTo.state
    const p =
        countryName === "Canada" || countryName === "United States"
            ? ContactService.normalizeState(String(rawState ?? "").trim(), states)
            : ""
    const province = p == null || p === "" ? "" : String(p)

    return {
        firstName: stripCommasFromName(pick(shipTo.first_name, base.firstName)),
        lastName: stripCommasFromName(pick(shipTo.last_name, base.lastName)),
        companyName: shipTo.company != null ? String(shipTo.company) : base.companyName,
        phone: pick(shipTo.phone, base.phone),
        email: pick(shipTo.email, base.email),
        country: countryName,
        province,
        city: shipTo.city != null ? String(shipTo.city) : "",
        streetAddress:
            shipTo.street_address != null ? String(shipTo.street_address) : "",
        postalCode: shipTo.zip != null ? String(shipTo.zip) : "",
    }
}

/** @param {object} c — row from GET /api/v2/customers */
export async function shippingFieldsFromApiCustomer(c) {
    const contactLike = {
        firstName: stripCommasFromName(c.first_name ?? ""),
        lastName: stripCommasFromName(c.last_name ?? ""),
        companyName: c.company ?? "",
        phone: c.phone ?? "",
        email: c.email ?? "",
    }
    return shippingFieldsFromShipTo(c.ship_to ?? null, contactLike)
}

/** @param {Record<string, unknown>} s — shipping section state */
export function billingFieldsFromShippingValues(s) {
    return {
        firstName: s.firstName ?? "",
        lastName: s.lastName ?? "",
        companyName: s.companyName ?? "",
        email: s.email ?? "",
        country: s.country ?? "",
        province: s.province ?? "",
        city: s.city ?? "",
        streetAddress: s.streetAddress ?? "",
        postalCode: s.postalCode ?? "",
        phone: s.phone ?? "",
    }
}

/**
 * Billing address from API customer `bill_to` (same shape as ship_to mapping).
 * @param {object} c — row from GET /api/v2/customers
 */
export async function billingFieldsFromApiCustomer(c) {
    const contactLike = {
        firstName: stripCommasFromName(c.first_name ?? ""),
        lastName: stripCommasFromName(c.last_name ?? ""),
        companyName: c.company ?? "",
        phone: c.phone ?? "",
        email: c.email ?? "",
    }
    const addr = await shippingFieldsFromShipTo(c.bill_to ?? null, contactLike)
    return addr
}
